import { ref, Ref } from 'vue'
import type { App } from 'vue'

// Базовый тип для WebSocket сообщений
export interface WebSocketReceiveMessage {
  signal: string
  [key: string]: unknown
}

// Тип для WebSocket сообщений с временной меткой
export interface WebSocketReceiveMessageTime extends WebSocketReceiveMessage {
  time?: number
}

// Тип для пустых WebSocket сообщений (только signal)
export interface WebSocketReceiveMessageEmpty<T extends string = string> {
  signal: T
}

// Тип для WebSocket сообщений с ошибкой
export interface WebSocketReceiveMessageError<T extends string = string> {
  signal: T
  reason: string
}

// Тип для WebSocket сообщений с данными
export interface WebSocketReceiveMessageData<T extends string = string, D = unknown> {
  signal: T
  data: D
}

// Утилиты для создания типов сообщений
export type WebSocketMessageEmpty<T extends string> = WebSocketReceiveMessageEmpty<T>
export type WebSocketMessageError<T extends string> = WebSocketReceiveMessageError<T>
export type WebSocketMessageData<T extends string, D = unknown> = WebSocketReceiveMessageData<T, D>

// Утилита для создания типа сообщения с дополнительными полями
export type WebSocketMessageWithFields<T extends string, F = Record<string, unknown>> = {
  signal: T
} & F

// Тип для WebSocket сообщений с временной меткой
export type WebSocketMessageTime<T extends string = string> = WebSocketReceiveMessageTime & {
  signal: T
}

// Тип для WebSocket сообщений с данными и временной меткой
export type WebSocketMessageWithDataAndTime<T extends string = string, D = Record<string, unknown>> = WebSocketReceiveMessageTime & {
  signal: T
} & D

// Утилиты для быстрого создания типов сообщений
export const createWebSocketTypes = <T extends string>(signal: T) => ({
  empty: () => ({ signal } as WebSocketMessageEmpty<T>),
  error: () => ({ signal, reason: '' } as WebSocketMessageError<T>),
  withFields: <F extends Record<string, unknown>>(fields: F) => ({ signal, ...fields } as WebSocketMessageWithFields<T, F>),
  withData: <D = unknown>(data: D) => ({ signal, data } as WebSocketMessageData<T, D>),
  withTime: () => ({ signal, time: 0 } as WebSocketMessageTime<T>)
});

// Готовые типы для часто используемых паттернов
export type WebSocketMessageSuccess<T extends string> = WebSocketMessageEmpty<T>
export type WebSocketMessageFail<T extends string> = WebSocketMessageError<T>
export type WebSocketMessageProgress<T extends string, P = Record<string, unknown>> = WebSocketMessageWithFields<T, P>

// Типы для WebSocket сообщений (для отправки)
export interface WebSocketSendMessage {
  action: string
  data: unknown
}

export interface WebSocketConfig {
  wsPort: number
  host: string
}

// Типы для обработчиков событий
export type WebSocketEventHandler = (event: Event) => void
export type WebSocketCloseHandler = (event: CloseEvent) => void
export type WebSocketMessageHandler = (event: MessageEvent) => void

// Состояние плагина
let wsPort: number | null = null
let connectOnDefinedPort = false
let socket: WebSocket | null = null
export const latestMessage: Ref<any> = ref(null)
let delaySend: WebSocketSendMessage[] = []
let lastSubscribeParams: any = null
let pluginInstalled = false
let reconnectTimeout: NodeJS.Timeout | null = null

// Обработчики по умолчанию
function defaultOnOpen(): void {
  for (let i = 0; i < delaySend.length; i++) {
    if (socket) {
      socket.send(JSON.stringify(delaySend[i]))
    }
  }
  delaySend = []
}

function defaultOnClose(event: CloseEvent): void {
  console.log('Default webSocket on close handler:', event)
}

function defaultOnError(error: Event): void {
  console.log('Default webSocket on error handler:', error)
}

function defaultOnMessage(event: MessageEvent): void {
  try {
    latestMessage.value = JSON.parse(event.data)
  } catch (error) {
    console.error('Error parsing WebSocket message:', error)
  }
}

// Текущие обработчики
let handlerOnOpen: WebSocketEventHandler = defaultOnOpen
let handlerOnClose: WebSocketCloseHandler = defaultOnClose
let handlerOnError: WebSocketEventHandler = defaultOnError
let handlerOnMessage: WebSocketMessageHandler = defaultOnMessage

// Инициализация WebSocket соединения
function initWebSocket(host: string, port: number): void {
  if (socket instanceof WebSocket && socket.readyState === WebSocket.OPEN) {
    return
  }

  // Закрываем старое соединение если есть
  if (socket instanceof WebSocket) {
    socket.close()
    // Даем время на закрытие
    setTimeout(() => {
      createNewWebSocket(host, port)
    }, 100)
    return
  }
  
  createNewWebSocket(host, port)
}

function createNewWebSocket(host: string, port: number): void {
  socket = new WebSocket(`wss://${host}:${port}/wss`)

  socket.addEventListener('open', handlerOnOpen)
  if (handlerOnOpen !== defaultOnOpen) {
    socket.addEventListener('open', defaultOnOpen)
  }
  socket.addEventListener('close', handlerOnClose)
  socket.addEventListener('error', handlerOnError)
  socket.addEventListener('message', handlerOnMessage)
  if (handlerOnMessage !== defaultOnMessage) {
    socket.addEventListener('message', defaultOnMessage)
  }
}



// Установка обработчиков событий
function setOnOpen(onOpen: WebSocketEventHandler): void {
  if (socket) {
    socket.removeEventListener('open', handlerOnOpen)
    if (handlerOnOpen !== defaultOnOpen) {
      socket.removeEventListener('open', defaultOnOpen)
    }
  }
  handlerOnOpen = onOpen
  if (socket) {
    socket.addEventListener('open', handlerOnOpen)
    if (handlerOnOpen !== defaultOnOpen) {
      socket.addEventListener('open', defaultOnOpen)
    }
  }
}

function setOnClose(onClose: WebSocketCloseHandler): void {
  if (socket) {
    socket.removeEventListener('close', handlerOnClose)
  }
  handlerOnClose = onClose
  if (socket) {
    socket.addEventListener('close', handlerOnClose)
  }
}

function setOnError(onError: WebSocketEventHandler): void {
  if (socket) {
    socket.removeEventListener('error', handlerOnError)
  }
  handlerOnError = onError
  if (socket) {
    socket.addEventListener('error', handlerOnError)
  }
}

function setOnMessage(onMessage: WebSocketMessageHandler): void {
  if (socket) {
    socket.removeEventListener('message', handlerOnMessage)
    if (handlerOnMessage !== defaultOnMessage) {
      socket.removeEventListener('message', defaultOnMessage)
    }
  }
  handlerOnMessage = onMessage
  if (socket) {
    socket.addEventListener('message', handlerOnMessage)
    if (handlerOnMessage !== defaultOnMessage) {
      socket.addEventListener('message', defaultOnMessage)
    }
  }
}

// Отправка сообщения
function sendMessage(data: WebSocketSendMessage): void {
  if (socket instanceof WebSocket && socket.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify(data))
  } else {
    delaySend.push(data)
  }
}

// Плагин
export const WebSocketPlugin = {
  install(app: App): void {
    if (pluginInstalled) {
      return
    }
    
    pluginInstalled = true
    fetch("/config.json")
      .then(response => response.json())
      .then((json: WebSocketConfig) => {
        wsPort = json.wsPort
        if (connectOnDefinedPort) {
          initWebSocket(window.location.hostname, wsPort)
          connectOnDefinedPort = false
        }
      })
      .catch(error => {
        console.error("Error fetching config:", error)
      })

    // Глобальные методы
    app.config.globalProperties.$wsReConnect = (): void => {
      // Защита от частых переподключений
      if (reconnectTimeout) {
        return
      }
      
      if (socket instanceof WebSocket && socket.readyState !== WebSocket.CLOSED) {
        socket.close()
      }
      
      if (wsPort !== null) {
        reconnectTimeout = setTimeout(() => {
          initWebSocket(window.location.hostname, wsPort!)
          reconnectTimeout = null
        }, 100)
      } else {
        connectOnDefinedPort = true
      }
    }

    app.config.globalProperties.$wsResubscribe = (): void => {
      if (lastSubscribeParams === null) {
        return
      }
      const data: WebSocketSendMessage = {
        action: 'subscribe',
        data: lastSubscribeParams,
      }
      sendMessage(data)
    }

    app.config.globalProperties.$wsSubscribe = (params: any): void => {
      const data: WebSocketSendMessage = {
        action: 'subscribe',
        data: params,
      }
      sendMessage(data)
    }

    app.config.globalProperties.$wsUnsubscribe = (keepLastSubscribe = true): void => {
      if (!keepLastSubscribe) {
        lastSubscribeParams = null
      }
      const data: WebSocketSendMessage = {
        action: 'unsubscribe',
        data: [],
      }
      sendMessage(data)
    }

    app.config.globalProperties.$wsSend = (action: string, params: any): void => {
      if (action === 'subscribe') {
        lastSubscribeParams = params
      }
      if (action === 'unsubscribe') {
        lastSubscribeParams = null
      }
      const data: WebSocketSendMessage = {
        action: action,
        data: params,
      }
      sendMessage(data)
    }

    app.config.globalProperties.$wsSetOnOpen = (onOpen: WebSocketEventHandler): void => {
      setOnOpen(onOpen)
    }

    app.config.globalProperties.$wsSetOnClose = (onClose: WebSocketCloseHandler): void => {
      setOnClose(onClose)
    }

    app.config.globalProperties.$wsSetOnError = (onError: WebSocketEventHandler): void => {
      setOnError(onError)
    }

    app.config.globalProperties.$wsSetOnMessage = (onMessage: WebSocketMessageHandler): void => {
      setOnMessage(onMessage)
    }
  }
} 